import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import {
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	ILocationState,
	TemplateOrTemplateFilter,
} from '../../../models';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { useModal } from '../../../models/hooks/useModal';
import { useEventLogging } from '../../../models/Logging';
import { hideDefaultMessageTemplate } from '../../../models/MessageTemplates';
import { PersistentStorageManager } from '../../../models/Storage';
import {
	invalidateAllMyTemplatesFilter,
	invalidateMyTemplates,
	useMyTemplatesFilter,
	useTemplateScopeMutation,
} from '../../../queries';
import {
	invalidateEmailAllCategoryTemplates,
	useEmailDeleteTemplate,
	useTemplateIndustryCategoriesQuery,
} from '../../../queries/EmailCampaign';
import { useTemplateKeywords } from '../../../queries/Templates/useEmailTemplateKeywords';
import { ITemplateCard, KnownCategories } from '../../../viewmodels/AppViewModels';
import { Button } from '../../components-v2/Button';
import { CampaignReportBanner } from '../../components/campaigns/CampaignReportBanner';
import { IConfirmationDialogOption } from '../../components/ConfirmationDialog';
import { DeleteConfirmation } from '../../components/DeleteConfirmation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Select';
import { ArrowIcon } from '../../components/svgs/icons/ArrowIcon';
import { TemplateSharingConfirmation } from '../../components/templates/TemplateSharingConfirmation';
import { baseStyleSheet, bs } from '../../styles/styles';
import { CampaignBrowser, useCampaignParams, useEnforceIndustryAndCategory } from '../CampaignBrowser';
import { useEmailCampaignBrowserData } from './hooks';
import { useTemplateFolders } from './hooks/useTemplateFolders';
import {
	ContentLibraryContent,
	ContentLibrarySectionHeader,
	ContentLibrarySectionHeaderTitle,
	emailCampaignSortByOptions,
	renderLoading,
	renderNotFound,
	SearchKeywords,
	SectionCards,
	SortBySelector,
	templateScopeFilterOptions,
} from './presentation';
import { styleSheet } from './styles';
import { TemplateFolderTabs } from './TemplateFolderTabs';
import { filterLevDraftTemplates } from './utils';

const invalidateTemplateQueries = () => {
	invalidateEmailAllCategoryTemplates();
	invalidateMyTemplates({ templateType: Api.TemplateType.Email });
	invalidateAllMyTemplatesFilter();
};

const EMAIL_TEMPLATES_SORT_PREFERENCE_KEY = 'emailTemplatesSortPreferences';
const EMAIL_REPORT_BANNER_DISMISSED_KEY = 'emailReportBannerDismissed';

const parseSortValue = (sortValue: string) => {
	if (sortValue === 'creationDate' || sortValue === 'creationDate_desc') {
		return { sortBy: 'creationDate', sort: 'desc' as const };
	}
	if (sortValue === 'creationDate_asc') {
		return { sortBy: 'creationDate', sort: 'asc' as const };
	}
	if (sortValue === 'name_asc') {
		return { sortBy: 'name', sort: 'asc' as const };
	}
	if (sortValue === 'name_desc') {
		return { sortBy: 'name', sort: 'desc' as const };
	}
	// Default fallback
	return { sortBy: 'creationDate', sort: 'desc' as const };
};

const formatSortValue = (sortBy: string, sort: 'asc' | 'desc') => {
	if (sortBy === 'creationDate') {
		return sort === 'asc' ? 'creationDate_asc' : 'creationDate_desc';
	}
	if (sortBy === 'name') {
		return sort === 'asc' ? 'name_asc' : 'name_desc';
	}
	return 'creationDate_desc';
};

const useEmailTemplatesSortPreferences = (userSession: Api.UserSessionContext) => {
	const getUserSortKey = React.useCallback(
		() => `${userSession?.user?.id || 'default'}_${EMAIL_TEMPLATES_SORT_PREFERENCE_KEY}`,
		[userSession?.user?.id]
	);

	const loadSortPreference = React.useCallback(() => {
		try {
			const stored = PersistentStorageManager.local.get(getUserSortKey());
			const defaultValue = 'creationDate_desc';
			const storedValue = stored ? JSON.parse(stored) : defaultValue;
			// Handle both old format (object) and new format (string)
			if (typeof storedValue === 'object' && storedValue.sortBy) {
				return formatSortValue(storedValue.sortBy, storedValue.sort);
			}
			// Handle legacy 'creationDate' value without direction
			if (storedValue === 'creationDate') {
				return 'creationDate_desc';
			}
			return storedValue || defaultValue;
		} catch {
			return 'creationDate_desc';
		}
	}, [getUserSortKey]);

	const saveSortPreference = React.useCallback(
		(sortValue: string) => {
			PersistentStorageManager.local.set(getUserSortKey(), JSON.stringify(sortValue));
		},
		[getUserSortKey]
	);

	return { loadSortPreference, saveSortPreference };
};

export const EmailCampaignCards = observer(() => {
	const { category, industry, search, setCampaignBrowserParam, sortBy } = useCampaignParams();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const { logApiError } = useEventLogging('EmailCampaignCards');
	const [scopeFilter, setScopeFilter] = React.useState<Api.TemplateScopeFilter>(Api.TemplateScopeFilter.All);
	const categoriesQuery = useTemplateIndustryCategoriesQuery({ industry });
	const { allCategoryTemplatesQuery, categoryTemplatesQuery, searchTemplatesQuery } =
		useEmailCampaignBrowserData(undefined);
	const shareEmailMutation = useTemplateScopeMutation();
	const deleteEmailMutation = useEmailDeleteTemplate();
	const [templateToBeManaged, setTemplateToBeManaged] = React.useState<ITemplateCard | undefined | null>();
	const [isBannerDismissed, setIsBannerDismissed] = useLocalStorage<boolean>({
		key: EMAIL_REPORT_BANNER_DISMISSED_KEY,
		initialValue: false,
	});

	const { loadSortPreference, saveSortPreference } = useEmailTemplatesSortPreferences(userSession);

	// Load sort preferences or use defaults
	const savedSortPreference = React.useMemo(() => loadSortPreference(), [loadSortPreference]);

	// Determine the effective sort value: URL param > localStorage > default
	const effectiveSortValue = sortBy || savedSortPreference;
	const parsedSort = parseSortValue(effectiveSortValue);
	const effectiveSortBy = parsedSort.sortBy;
	const effectiveSort = parsedSort.sort;

	const selectedSortByCampaignParam =
		emailCampaignSortByOptions.find(i => i.value === effectiveSortValue)?.value || emailCampaignSortByOptions[0].value;

	const [keywordsSearchValue, setKeywordsSearchValue] = React.useState<string>('');
	const debouncedKeywordsSearchValue = useDebounceValue(keywordsSearchValue, 500);

	const myTemplatesFilterQuery = useMyTemplatesFilter({
		enabled: true,
		industry,
		term: debouncedKeywordsSearchValue,
		scopeFilter,
		sortBy: effectiveSortBy,
		sort: effectiveSort,
	});

	// Memoize filtered templates at component level
	const isCustomerImpersonation = userSession?.isCustomerImpersonation;
	const filteredMyTemplates = React.useMemo(
		() => filterLevDraftTemplates(myTemplatesFilterQuery.data || [], isCustomerImpersonation),
		[myTemplatesFilterQuery.data, isCustomerImpersonation]
	);

	// Fetch all keywords for folder extraction
	const keywordsQuery = useTemplateKeywords({
		industry,
		enabled: !debouncedKeywordsSearchValue && category === KnownCategories.MyTemplates,
	});

	// Folder filtering for My Templates
	const {
		folders,
		selectedFolder,
		setSelectedFolder,
		filteredTemplates: folderFilteredTemplates,
	} = useTemplateFolders({
		templates: filteredMyTemplates,
		allKeywords: keywordsQuery.data ?? [],
	});

	// Filter My Templates for the "All" page section
	const filteredMyTemplatesAllPage = React.useMemo(
		() => filterLevDraftTemplates(allCategoryTemplatesQuery?.data?.Me || [], isCustomerImpersonation),
		[allCategoryTemplatesQuery?.data?.Me, isCustomerImpersonation]
	);

	let categories: string[] = [];
	if (categoriesQuery.data) {
		categories = categoriesQuery.data;
		categories = categories.filter(
			c =>
				c !== 'Archived' &&
				c !== 'Uncategorized' &&
				(industry === Api.Industry.Financial ? c !== KnownCategories.FinraReviewed : true)
		);
		categories.splice(0, 0, KnownCategories.All);
		categories.splice(2, 0, KnownCategories.MyTemplates);
		if (industry === Api.Industry.Financial) {
			const indexOfFeatured = categories.findIndex(x => x === KnownCategories.Featured) || 0;
			categories.splice(indexOfFeatured + 1, 0, KnownCategories.FinraReviewed);
		}
	}

	// #region Delete
	const canDelete = React.useCallback(
		(cardOwnerId: string) => {
			if (userSession.isAdmin) {
				return true;
			}

			if (!cardOwnerId) {
				return false;
			}

			return cardOwnerId === userSession.user?.id;
		},
		[userSession.user?.id, userSession.isAdmin]
	);

	useEnforceIndustryAndCategory({ setCampaignBrowserParam, tabUrlParam: 'email' });

	const deleteConfirmationModalProps = useModal(
		false,
		async (shouldDelete: boolean | undefined) => {
			if (shouldDelete) {
				try {
					if (canDelete(templateToBeManaged?.creator?.id)) {
						await deleteEmailMutation.mutateAsync({
							template: templateToBeManaged,
						});
						toast.success('Template deleted successfully');
						invalidateTemplateQueries();
					} else if (templateToBeManaged?.id) {
						hideDefaultMessageTemplate(templateToBeManaged?.id);
					}
				} catch (error: any) {
					errorMessages.pushApiError(error);
					logApiError('TemplateShare-Error', error);
					toast.error('Error deleting template');
				}
			}

			setTemplateToBeManaged(null);
		},
		[templateToBeManaged, deleteEmailMutation]
	);

	const onDeleteClick = (templateCard: ITemplateCard) => {
		setTemplateToBeManaged(templateCard);
		deleteConfirmationModalProps.setIsOpen(true)();
	};
	// #endregion

	// #region Share
	const [shareTemplate, setShareTemplate] = React.useState<boolean | null>(false);

	const shareConfirmationModalProps = useModal(
		false,
		async (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
			if (templateToBeManaged && result?.representedObject && !canceled) {
				try {
					await shareEmailMutation.mutateAsync({
						scope: shareTemplate ? Api.TemplateScope.Account : Api.TemplateScope.User,
						id: templateToBeManaged.id,
					});
					invalidateTemplateQueries();
					toast.success(`Template ${shareTemplate ? 'shared' : 'unshared'} successfully`);
				} catch (error: any) {
					errorMessages.pushApiError(error);
					logApiError('TemplateShare-Error', error);
					toast.error(`Error ${shareTemplate ? 'sharing' : 'unsharing'} template`);
				}
			}
			setShareTemplate(null);
			setTemplateToBeManaged(null);
		},
		[shareTemplate, templateToBeManaged, shareEmailMutation]
	);

	const onShareClick = (templateCard: ITemplateCard) => (isShared: boolean) => {
		setShareTemplate(isShared);
		setTemplateToBeManaged(templateCard);
		shareConfirmationModalProps.setIsOpen(true)();
	};
	// #endregion

	const onViewCampaign = async (templateId: string, socialIndicator?: boolean) => {
		try {
			const templates = new Api.TemplatesViewModel(userSession);
			const template = await templates.getByIdExpandedByLastUsedBy(templateId);
			if (socialIndicator) {
				const associatedTemplateId = template?.associatedTemplates?.[0]?.id ?? '';
				const locationState: ILocationState<any, ICreateSocialMediaPostRequest<string | Api.ITemplate>> = {
					model: {
						context: associatedTemplateId,
						type: 'Template',
					},
				};
				fullscreenModal?.history.push({
					pathname: '/social-media/post/create/from-template',
					state: locationState,
				});
			} else {
				const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
					model: {
						context: { ...template },
						type: 'Template',
					},
				};

				fullscreenModal?.history.push({
					pathname: '/email/campaigns/create/from-template',
					state: locationState,
				});
			}
		} catch (error: any) {
			errorMessages.pushApiError(error);
			logApiError('LoadCategories-Error', error);
		}
	};

	const renderMainBrowser = () => {
		if (category === KnownCategories.All && !search) {
			if (allCategoryTemplatesQuery.status !== 'success') {
				return renderLoading();
			}
			return (
				<ContentLibraryContent>
					{(allCategoryTemplatesQuery?.data?.Featured ?? []).length > 0 ? (
						<SectionCards
							title='Featured Campaigns'
							templates={allCategoryTemplatesQuery?.data?.Featured}
							industry={industry}
							header={
								<ContentLibrarySectionHeader>
									<ContentLibrarySectionHeaderTitle>Featured Campaigns</ContentLibrarySectionHeaderTitle>
									<Button
										variant='link'
										size='sm'
										onClick={() => setCampaignBrowserParam({ category: KnownCategories.Featured })}
									>
										Browse all featured campaigns <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
									</Button>
								</ContentLibrarySectionHeader>
							}
							onViewCampaign={onViewCampaign}
							onDeleteClick={onDeleteClick}
							onShareClick={onShareClick}
							areAccountTemplates={false}
						/>
					) : null}
					{(allCategoryTemplatesQuery?.data?.['FINRA Reviewed'] ?? []).length > 0 ? (
						<>
							<SectionCards
								title='FINRA Reviewed'
								templates={allCategoryTemplatesQuery?.data?.['FINRA Reviewed']}
								industry={industry}
								header={
									<ContentLibrarySectionHeader>
										<ContentLibrarySectionHeaderTitle>FINRA Reviewed</ContentLibrarySectionHeaderTitle>
										<Button
											variant='link'
											size='sm'
											onClick={() =>
												setCampaignBrowserParam({
													category: KnownCategories.FinraReviewed,
												})
											}
										>
											Browse all FINRA Reviewed campaigns <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
										</Button>
									</ContentLibrarySectionHeader>
								}
								onViewCampaign={onViewCampaign}
								onDeleteClick={onDeleteClick}
								onShareClick={onShareClick}
								areAccountTemplates={false}
							/>
						</>
					) : null}

					{filteredMyTemplatesAllPage.length > 0 ? (
						<>
							<SectionCards
								category={KnownCategories.MyTemplates}
								title='My Templates'
								templates={filteredMyTemplatesAllPage}
								industry={industry}
								header={
									<ContentLibrarySectionHeader>
										<ContentLibrarySectionHeaderTitle>My Templates</ContentLibrarySectionHeaderTitle>
										<Button
											variant='link'
											size='sm'
											onClick={() =>
												setCampaignBrowserParam({
													category: KnownCategories.MyTemplates,
												})
											}
										>
											Browse my templates
											<ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
										</Button>
									</ContentLibrarySectionHeader>
								}
								onViewCampaign={onViewCampaign}
								onDeleteClick={onDeleteClick}
								onShareClick={onShareClick}
								areAccountTemplates={true}
							/>
						</>
					) : null}
					{categories.map(c => {
						if (
							c === KnownCategories.Featured ||
							c === KnownCategories.All ||
							c === KnownCategories.MyTemplates ||
							c === KnownCategories.FinraReviewed
						) {
							return null;
						}
						return (
							<React.Fragment key={c}>
								<SectionCards
									key={c}
									title={`${c.split('-').join(' ')} Campaigns`}
									templates={allCategoryTemplatesQuery?.data?.[c]}
									industry={industry}
									header={
										<ContentLibrarySectionHeader>
											<ContentLibrarySectionHeaderTitle>{`${c.split('-').join(' ')} Campaigns`}</ContentLibrarySectionHeaderTitle>
											<Button
												variant='link'
												size='sm'
												onClick={() =>
													setCampaignBrowserParam({
														category: c as KnownCategories,
													})
												}
											>
												{`Browse ${c.split('-').join(' ')} Campaigns`}
												<ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
											</Button>
										</ContentLibrarySectionHeader>
									}
									onViewCampaign={onViewCampaign}
									onDeleteClick={onDeleteClick}
									onShareClick={onShareClick}
									areAccountTemplates={c === KnownCategories.MyTemplates}
								/>
							</React.Fragment>
						);
					})}
				</ContentLibraryContent>
			);
		}
		return null;
	};

	const handleSortBy = (option: string) => {
		// Save the preference to localStorage
		saveSortPreference(option);
		// Update the URL parameter
		setCampaignBrowserParam({ sortBy: option });
	};

	const handleSearchKeywordsInputChange = (searchValue: string) => {
		setKeywordsSearchValue(searchValue);
	};

	const handleScopeFilterChange = (value: string) => {
		setScopeFilter(value as Api.TemplateScopeFilter);
	};

	const handleBrowseAllCampaignsClick = () => {
		setKeywordsSearchValue('');
		setCampaignBrowserParam({ category: KnownCategories.All });
	};

	const renderTemplates = (templatesToDisplay: ITemplateCard[], isLoading?: boolean) => {
		return (
			<ContentLibraryContent>
				{!search ? (
					<ContentLibrarySectionHeader>
						<ContentLibrarySectionHeaderTitle>
							{category === KnownCategories.MyTemplates
								? KnownCategories.MyTemplates
								: `${String(category).split('-').join(' ')} Campaigns`}
						</ContentLibrarySectionHeaderTitle>
						{!search ? (
							<Button
								variant='link'
								size='sm'
								onClick={() => setCampaignBrowserParam({ category: KnownCategories.All })}
							>
								Browse all campaigns <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
							</Button>
						) : null}
					</ContentLibrarySectionHeader>
				) : null}
				{templatesToDisplay?.length > 0 ? (
					<SectionCards
						category={category as KnownCategories}
						title=''
						templates={templatesToDisplay}
						industry={industry}
						onViewCampaign={onViewCampaign}
						onDeleteClick={onDeleteClick}
						onShareClick={onShareClick}
						areAccountTemplates={category === KnownCategories.MyTemplates && !search}
					/>
				) : isLoading ? (
					renderLoading()
				) : (
					renderNotFound()
				)}
			</ContentLibraryContent>
		);
	};
	const renderSearchTemplates = () => {
		if (search) {
			if (searchTemplatesQuery.status !== 'success') {
				return renderLoading();
			}
			return renderTemplates(searchTemplatesQuery.data);
		}
		return null;
	};
	const renderMyTemplates = () => {
		if (search || category !== KnownCategories.MyTemplates) {
			return null;
		}

		return (
			<ContentLibraryContent>
				<div className={css(styleSheet.flexDiv)}>
					<h2 className={css(styleSheet.categoryTitle)}>
						{category === KnownCategories.MyTemplates
							? KnownCategories.MyTemplates
							: `${String(category).split('-').join(' ')} Campaigns`}
					</h2>
					<div className={css(styleSheet.filtersContainer)}>
						<Select items={templateScopeFilterOptions} value={scopeFilter} onValueChange={handleScopeFilterChange}>
							<SelectTrigger styles={[styleSheet.templateScopeSelect]}>
								<SelectValue placeholder='Filter templates...' />
							</SelectTrigger>
							<SelectContent>
								{templateScopeFilterOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<SortBySelector onChange={handleSortBy} selectedValue={selectedSortByCampaignParam} />
					<SearchKeywords onChange={handleSearchKeywordsInputChange} value={keywordsSearchValue} />
					<Button variant='link' size='sm' onClick={handleBrowseAllCampaignsClick}>
						Browse all campaigns <ArrowIcon fillColor='currentColor' styles={[bs.mt1]} />
					</Button>
				</div>
				{/* Folder tabs - hidden during keyword search */}
				{!debouncedKeywordsSearchValue && folders.length > 0 && (
					<TemplateFolderTabs folders={folders} selectedFolder={selectedFolder} onFolderSelect={setSelectedFolder} />
				)}
				{folderFilteredTemplates?.length > 0 ? (
					<>
						<SectionCards
							category={category as KnownCategories}
							title=''
							templates={folderFilteredTemplates}
							industry={industry}
							onViewCampaign={onViewCampaign}
							onDeleteClick={onDeleteClick}
							onShareClick={onShareClick}
							areAccountTemplates={category === KnownCategories.MyTemplates && !search}
						/>
					</>
				) : myTemplatesFilterQuery.isPending ? (
					renderLoading()
				) : (
					renderNotFound()
				)}
			</ContentLibraryContent>
		);
	};
	const renderCategoryTemplates = () => {
		if (search || category === KnownCategories.All || category === KnownCategories.MyTemplates) {
			return null;
		}
		if (categoryTemplatesQuery.status !== 'success') {
			return renderLoading();
		}
		return renderTemplates(categoryTemplatesQuery.data);
	};

	const handleSearchChange = React.useCallback(
		(value: string) => {
			setCampaignBrowserParam({ search: value });
		},
		[setCampaignBrowserParam]
	);
	return (
		<CampaignBrowser
			category={category}
			categories={categories}
			industry={industry}
			search={search}
			onSearchChange={handleSearchChange}
			onCategoryChange={value => setCampaignBrowserParam({ category: value })}
			onIndustryChange={value =>
				setCampaignBrowserParam({
					industry: value,
					category: KnownCategories.All,
				})
			}
		>
			{!isBannerDismissed && (
				<CampaignReportBanner
					styles={[bs.mt2]}
					description={
						<>
							See what you have scheduled to go out vs how past campaigns have performed in the&nbsp;
							<NavLink className={css(baseStyleSheet.brandLink)} to='/reporting/group-email'>
								Email Reports
							</NavLink>
							&nbsp;section.
						</>
					}
					onClose={() => setIsBannerDismissed(true)}
				/>
			)}
			{renderMainBrowser()}
			{renderSearchTemplates()}
			{renderMyTemplates()}
			{renderCategoryTemplates()}
			<DeleteConfirmation
				bodyText='Are you sure you want to delete this template?'
				destructiveButtonText='Yes, Delete'
				isOpen={deleteConfirmationModalProps.isOpen}
				onFinish={deleteConfirmationModalProps.onRequestClose}
			/>
			<TemplateSharingConfirmation
				onRequestClose={shareConfirmationModalProps.onRequestClose}
				show={shareConfirmationModalProps.isOpen}
				template={templateToBeManaged ?? undefined}
			/>
		</CampaignBrowser>
	);
});
